import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"
import { setButtonLoading } from "../lib/button_loading"
import { showConfirmModal } from "../lib/custom_confirm"

// Build Now on Admin → Reports → Lead Demand (#16893). Queues one manual
// Lead Demand build (the same job and lock as the schedule); while a build
// runs or waits the button is disabled and busy, its label unchanged
// (BUTTON-8), with "Building… started 10:42 AM ET" beside it. It polls the
// build status and opens the new report when one lands. "New" is judged
// against the newest valid build when the page loaded, never the build picked
// from the Earlier Build history, and the report opens without that pick.
// A build takes minutes, so Build Now asks first (#17738, owner: "this button
// should not have run without asking me"); Cancel posts nothing.
const BUSY_STATES = ["running", "queued"]
// Every demand figure is counted on the Eastern clock (LeadDemand::RunHours).
const ZONE = "America/New_York"
const NO_NEW_REPORT = "The build finished without a new valid report"
export const CONFIRM_MESSAGE =
  "A build reads every open order and takes a few minutes. The page opens the new report when it lands."
export const CONFIRM_OPTIONS = { title: "Build a new report now?", confirmText: "Build Now", variant: "primary" }

export default class extends Controller {
  static POLL_MS = 5000
  static targets = ["button", "status"]
  static values = {
    buildUrl: String,
    statusUrl: String,
    reportUrl: String,
    latestSnapshotId: String,
    state: String,
    startedAt: String
  }

  connect() {
    if (BUSY_STATES.includes(this.stateValue)) this.showBusy(this.startedAtValue)
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  async build() {
    if (this.buttonTarget.disabled) return
    if (!(await showConfirmModal(CONFIRM_MESSAGE, CONFIRM_OPTIONS))) return
    if (this.buttonTarget.disabled) return

    this.showBusy(new Date().toISOString())
    const response = await post(this.buildUrlValue, { responseKind: "json" })
    if (!response.ok) return this.showIdle("The build could not be started")

    const status = await response.json
    this.showBusy(status.started_at)
  }

  async poll() {
    const response = await get(this.statusUrlValue, { responseKind: "json" })
    if (!response.ok) return this.schedule()

    const status = await response.json
    if (BUSY_STATES.includes(status.state)) return this.schedule()
    if (status.snapshot_id && String(status.snapshot_id) !== this.latestSnapshotIdValue) return this.openNewReport()

    this.showIdle(NO_NEW_REPORT)
  }

  openNewReport() {
    window.location.assign(this.reportUrlValue)
  }

  // private

  showBusy(startedAt) {
    setButtonLoading(this.buttonTarget, true)
    this.statusTarget.textContent = startedAt ? `Building… started ${this.clock(startedAt)} ET` : "Building…"
    this.schedule()
  }

  showIdle(message) {
    clearTimeout(this.timer)
    setButtonLoading(this.buttonTarget, false)
    this.statusTarget.textContent = message
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.poll(), this.constructor.POLL_MS)
  }

  clock(iso) {
    return new Intl.DateTimeFormat("en-US", { timeZone: ZONE, hour: "numeric", minute: "2-digit" })
      .format(new Date(iso))
  }
}
